import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Clock3, HelpCircle, Info, WifiOff, X } from 'lucide-react';
import { freshnessOf } from '@karhutla/contracts';

export function Panel({ title, eyebrow, action, children, className = '', id }: { title?: ReactNode; eyebrow?: string; action?: ReactNode; children: ReactNode; className?: string; id?: string }) {
  return <section className={`panel ${className}`} id={id}>
    {(title || action) && <header className="panel-header"><div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h2>{title}</h2></div>{action}</header>}
    {children}
  </section>;
}

export function Badge({ children, tone = 'neutral', pulse = false }: { children: ReactNode; tone?: string; pulse?: boolean }) {
  return <span className={`badge badge-${tone}${pulse ? ' badge-pulse' : ''}`}><i />{children}</span>;
}

export function Freshness({ at, compact = false }: { at?: string; compact?: boolean }) {
  const state = freshnessOf(at, Date.parse('2026-09-02T14:30:00Z'));
  const minutes = at ? Math.max(0, Math.round((Date.parse('2026-09-02T14:30:00Z') - Date.parse(at)) / 60_000)) : null;
  return <span className={`freshness freshness-${state}`} title={at ?? 'No observation time'}><Clock3 size={12} /> {compact ? state : minutes === null ? 'Age unknown' : minutes < 1 ? 'Updated now' : `${minutes} min old · ${state}`}</span>;
}

export function MetricCard({ label, value, detail, trend, tone = 'default', icon }: { label: string; value: ReactNode; detail?: ReactNode; trend?: string; tone?: string; icon?: ReactNode }) {
  return <article className={`metric-card metric-${tone}`}>
    <div className="metric-top"><span>{label}</span>{icon}</div>
    <strong>{value}</strong>
    <div className="metric-bottom"><span>{detail}</span>{trend && <span className={trend.startsWith('+') ? 'trend-up' : 'trend-down'}>{trend}</span>}</div>
  </article>;
}

export function Progress({ value, tone = 'green', label }: { value: number; tone?: string; label?: string }) {
  return <div className="progress-wrap">{label && <div className="progress-label"><span>{label}</span><b>{Math.round(value)}%</b></div>}<div className="progress"><span className={`progress-${tone}`} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} /></div></div>;
}

export function MiniBars({ values, tone = 'green' }: { values: number[]; tone?: string }) {
  const max = Math.max(...values, 1);
  return <div className="mini-bars" aria-label={`Values ${values.join(', ')}`}>{values.map((value, index) => <i key={index} className={`bar-${tone}`} style={{ height: `${Math.max(8, value / max * 100)}%` }} />)}</div>;
}

export function EmptyState({ title, text, type = 'info' }: { title: string; text: string; type?: 'info' | 'warning' | 'offline' | 'success' }) {
  const Icon = type === 'warning' ? AlertTriangle : type === 'offline' ? WifiOff : type === 'success' ? CheckCircle2 : Info;
  return <div className={`empty-state empty-${type}`}><Icon size={24} /><div><b>{title}</b><p>{text}</p></div></div>;
}

export function HelpButton({ onClick, label = 'Open help' }: { onClick?: () => void; label?: string }) {
  return <button className="icon-button" onClick={onClick} aria-label={label} title={label}><HelpCircle size={17} /></button>;
}

export function Modal({ title, children, onClose, footer }: { title: string; children: ReactNode; onClose: () => void; footer?: ReactNode }) {
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><div className="modal" role="dialog" aria-modal="true" aria-label={title}><header><h2>{title}</h2><button className="icon-button" onClick={onClose} aria-label="Close"><X size={18} /></button></header><div className="modal-body">{children}</div>{footer && <footer>{footer}</footer>}</div></div>;
}

export function ProbabilityRing({ value, label }: { value: number; label: string }) {
  return <div className="probability-ring" style={{ '--value': `${value * 360}deg` } as React.CSSProperties}><div><strong>{Math.round(value * 100)}%</strong><span>{label}</span></div></div>;
}

export function Segmented({ options, value, onChange }: { options: Array<{ id: string; label: string }>; value: string; onChange: (id: string) => void }) {
  return <div className="segmented">{options.map((option) => <button key={option.id} className={value === option.id ? 'active' : ''} onClick={() => onChange(option.id)}>{option.label}</button>)}</div>;
}
